import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import AppleLogin from "react-apple-login";
import "./styles.css";
import Logo from "../../assets/Logo.jpeg";
import { API_BASE_URL, appleRedirectUri } from "../../config/baseUrls";

// Where to go after login: only our own /events pages (no open redirect). Kept in
// sessionStorage too, for the Apple redirect flow that comes back via /events/auth/callback.
export const EVENT_LOGIN_NEXT = "eventLoginNext";
const safeNext = (next) =>
  typeof next === "string" && next.startsWith("/events/") && !next.startsWith("/events/login")
    ? next
    : null;
export const eventLoginTarget = () =>
  safeNext(sessionStorage.getItem(EVENT_LOGIN_NEXT)) || "/events/checkout";

const EventLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authProvider, setAuthProvider] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [notVerified, setNotVerified] = useState(false);
  const [info, setInfo] = useState(null);

  const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
  const REDIRECT_URI = appleRedirectUri(
    process.env.REACT_APP_REDIRECT_URI ||
      window.location.origin + "/events/auth/callback"
  );
  const BACKEND_URL = API_BASE_URL;

  useEffect(() => {
    const next = safeNext(searchParams.get("next"));
    if (next) sessionStorage.setItem(EVENT_LOGIN_NEXT, next);
    if (localStorage.getItem("JWTtoken")) {
      navigate(eventLoginTarget(), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendTokenToBackend = async (idToken, provider) => {
    try {
      setLoading(true);
      setAuthProvider(provider);

      const response = await fetch(`${BACKEND_URL}/${provider}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: idToken,
        }),
      });

      if (!response.ok) {
        throw new Error("Backend authentication failed");
      }

      const data = await response.json();

      localStorage.setItem("JWTtoken", data.token);
      localStorage.setItem("userId", data.user.cuid);

      await checkUserProfile(data.user.cuid, data.token);
    } catch (error) {
      console.error(`${provider} authentication error:`, error);
      setError(
        `${
          provider.charAt(0).toUpperCase() + provider.slice(1)
        } authentication failed`
      );
    } finally {
      setLoading(false);
      setAuthProvider(null);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setNotVerified(false);
    setLoading(true);
    setAuthProvider("email");
    try {
      const response = await fetch(`${BACKEND_URL}/content/signin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (data.code === "EMAIL_NOT_VERIFIED") {
          setNotVerified(true);
          setError("Please verify your email before signing in. Check your inbox for the link.");
        } else if (response.status === 402) {
          setError("Incorrect email or password.");
        } else if (response.status === 429) {
          setError("Too many attempts. Please wait a minute and try again.");
        } else {
          setError("Sign in failed. Please try again.");
        }
        return;
      }

      localStorage.setItem("JWTtoken", data.token);
      localStorage.setItem("userId", data.user.cuid);
      await checkUserProfile(data.user.cuid, data.token);
    } catch (err) {
      console.error("Email sign in error:", err);
      setError("Sign in failed. Please try again.");
    } finally {
      setLoading(false);
      setAuthProvider(null);
    }
  };

  const handleResendVerification = async () => {
    try {
      await fetch(`${BACKEND_URL}/content/resendVerificationEmail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      setError(null);
      setInfo("Verification email sent. Check your inbox, then sign in.");
    } catch (err) {
      setError("Could not send the email. Please try again.");
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError("Enter your email above first.");
      return;
    }
    try {
      await fetch(`${BACKEND_URL}/content/forgotpassword`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      setError(null);
      setInfo("If an account exists for this email, a reset link has been sent.");
    } catch (err) {
      setError("Could not send the email. Please try again.");
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    sendTokenToBackend(credentialResponse.credential, "google");
  };

  const handleGoogleError = () => {
    setError("Google authentication failed. Please try again.");
    setLoading(false);
    setAuthProvider(null);
  };

  const handleAppleSuccess = (response) => {
    sendTokenToBackend(response.authorization.id_token, "apple");
  };

  const handleAppleError = (error) => {
    console.error("Apple authentication error:", error);
    setError("Apple authentication failed. Please try again.");
    setLoading(false);
    setAuthProvider(null);
  };

  const checkUserProfile = async (userId, token) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/auth/getuserprod?cuid=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await response.json();

      navigate(eventLoginTarget(), { replace: true });
    } catch (error) {
      console.error("Error checking user profile:", error);
      navigate("/events/login");
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo">
            <img src={Logo} alt="Logo" />
          </div>
          <h1 className="login-title">Welcome To Ota Life</h1>
          <p className="login-subtitle">Sign in to continue to your account</p>
        </div>

        {info && <div className="info-banner">{info}</div>}

        {error && (
          <div className="error-banner">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                fill="currentColor"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <div className="login-buttons">
          <div className="google-login-wrapper">
            {loading && authProvider === "google" ? (
              <button className="login-btn google-btn" disabled>
                <div className="btn-loader"></div>
              </button>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                theme="outline"
                size="large"
                text="continue_with"
                shape="rectangular"
                logo_alignment="left"
              />
            )}
          </div>

          {loading && authProvider === "apple" ? (
            <button className="login-btn apple-btn" disabled>
              <div className="btn-loader"></div>
            </button>
          ) : (
            <AppleLogin
              clientId={APPLE_CLIENT_ID}
              redirectURI={REDIRECT_URI}
              scope="email name"
              responseMode="form_post"
              usePopup={true}
              callback={handleAppleSuccess}
              onError={handleAppleError}
              render={(props) => (
                <button {...props} className="login-btn apple-btn">
                  <svg
                    className="btn-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <span>Continue with Apple</span>
                </button>
              )}
            />
          )}
        </div>

        <div className="email-divider"><span>or</span></div>

        <form className="email-form" onSubmit={handleEmailSignIn}>
          <input
            type="email"
            className="email-input"
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            className="email-input"
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="submit"
            className="login-btn email-btn"
            disabled={loading}
          >
            {loading && authProvider === "email" ? (
              <div className="btn-loader"></div>
            ) : (
              <span>Sign in with email</span>
            )}
          </button>
          <div className="email-links">
            <button type="button" className="email-link" onClick={handleForgotPassword}>
              Forgot password?
            </button>
            {notVerified && (
              <button type="button" className="email-link" onClick={handleResendVerification}>
                Resend verification email
              </button>
            )}
          </div>
          <p className="email-note">
            No account yet? Sign up in the Ōta LIFE app first, or continue with Google or Apple.
          </p>
        </form>
      </div>

      <div className="login-background">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
      </div>
    </div>
  );
};

export default EventLogin;
